import { useState } from "react";
import { UserPlus, Trash2, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TeamMemberDialog } from "./TeamMemberDialog";
import { useTeamMembers, useRemoveTeamMember } from "@/hooks/useTeamMembers";
import { useToast } from "@/hooks/useToast";

interface TeamMembersSectionProps {
  teamId: string;
  teamName: string;
}

export function TeamMembersSection({
  teamId,
  teamName,
}: TeamMembersSectionProps) {
  const { data: members = [] } = useTeamMembers(teamId);
  const removeTeamMember = useRemoveTeamMember();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  const handleRemove = async () => {
    if (!removingMemberId) return;
    try {
      await removeTeamMember.mutateAsync({
        id: removingMemberId,
        teamId,
      });
      toast({ title: "Membre retiré de l'équipe" });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de retirer le membre",
        variant: "destructive",
      });
    } finally {
      setRemovingMemberId(null);
    }
  };

  const removingMember = members.find((m) => m.id === removingMemberId);

  return (
    <>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              <span className="text-sm font-medium">
                Membres ({members.length})
              </span>
            </Button>
          </CollapsibleTrigger>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDialogOpen(true)}
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Ajouter
          </Button>
        </div>
        <CollapsibleContent className="space-y-2 mt-2">
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              Aucun membre dans cette équipe
            </p>
          ) : (
            <div className="space-y-2">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between rounded-lg border p-2"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {member.user.full_name || member.user.email}
                      </span>
                      {member.is_leader && (
                        <Crown className="h-3 w-3 text-yellow-500" />
                      )}
                    </div>
                    <div className="flex gap-2 mt-1">
                      {member.role_in_team && (
                        <Badge variant="secondary" className="text-xs">
                          {member.role_in_team}
                        </Badge>
                      )}
                      {member.instrument && (
                        <Badge variant="outline" className="text-xs">
                          {member.instrument}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRemovingMemberId(member.id)}
                  >
                    <Trash2 className="h-3 w-3 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <TeamMemberDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        teamId={teamId}
      />

      <AlertDialog
        open={!!removingMemberId}
        onOpenChange={() => setRemovingMemberId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer le membre</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir retirer{" "}
              {removingMember?.user.full_name || removingMember?.user.email} de
              l'équipe {teamName} ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove}>
              Retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
